import React, { useState } from "react";
import Page from "../../components/Page";
import {
  IconTruck,
  IconSearch,
  IconFilter,
  IconCheck,
  IconX,
  IconCalendar,
  IconBuildingStore,
  IconEye,
  IconPackage,
  IconChevronsLeft,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsRight,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import {
  useProviderSupplyOrders,
  markProviderOrderDispatched,
  useProviderSupplyOrderDetail,
} from "../../controllers/requisition.controller";
import toast from "react-hot-toast";

export default function ProviderSupplyOrdersPage() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Selected Order for Dispatch Modal
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);
  const [dispatchNotes, setDispatchNotes] = useState("");
  const [itemDispatches, setItemDispatches] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { orders, total, isLoading, mutate } = useProviderSupplyOrders({
    status: selectedStatus,
    from: fromDate,
    to: toDate,
    page,
    limit: pageSize,
  });

  const { order: orderDetail, isLoading: isLoadingDetail } = useProviderSupplyOrderDetail(selectedOrderId);

  const totalPages = Math.ceil(total / pageSize) || 1;

  const handleOpenDispatchModal = (order) => {
    setSelectedOrderId(order.id);
    setDispatchNotes("");
    setIsDispatchModalOpen(true);
  };

  // Sync item dispatches when detail loads
  React.useEffect(() => {
    if (orderDetail?.items) {
      setItemDispatches(
        orderDetail.items.map((it) => ({
          id: it.id,
          item_title: it.item_title,
          approved_quantity: Number(it.approved_quantity) || 0,
          dispatched_quantity:
            it.dispatched_quantity !== null
              ? Number(it.dispatched_quantity)
              : Number(it.approved_quantity) || 0,
        }))
      );
    }
  }, [orderDetail]);

  const handleConfirmDispatch = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        notes: dispatchNotes,
        items: itemDispatches.map((it) => ({
          id: it.id,
          dispatched_quantity: Number(it.dispatched_quantity) || 0,
        })),
      };

      await markProviderOrderDispatched(selectedOrderId, payload);
      toast.success("Supply order marked as dispatched to restaurant");
      setIsDispatchModalOpen(false);
      setSelectedOrderId(null);
      mutate();
    } catch (err) {
      console.error("Dispatch error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to mark dispatched");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page className="p-4 md:p-6 space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <IconTruck className="text-purple-600" stroke={iconStroke} size={28} />
            Provider Supply Orders
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Fulfill and dispatch ingredient orders forwarded by restaurant tenants
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-neutral-700 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Status Select */}
          <select
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setPage(1);
            }}
            className="select select-bordered select-sm w-full rounded-xl bg-gray-50 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-sm"
          >
            <option value="all">All Supply Orders</option>
            <option value="provider_ordered">Ordered (Awaiting Dispatch)</option>
            <option value="provider_dispatched">Dispatched (En Route)</option>
            <option value="received">Delivered & Confirmed</option>
          </select>

          {/* Date From */}
          <div className="flex items-center gap-1 bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-xl px-2">
            <span className="text-xs text-gray-400">From:</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="bg-transparent border-none text-xs w-full py-1 text-gray-700 dark:text-gray-200 focus:outline-none"
            />
          </div>

          {/* Date To */}
          <div className="flex items-center gap-1 bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-xl px-2">
            <span className="text-xs text-gray-400">To:</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="bg-transparent border-none text-xs w-full py-1 text-gray-700 dark:text-gray-200 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full text-sm">
            <thead className="bg-gray-50/80 dark:bg-neutral-900/60 text-xs font-semibold uppercase text-gray-500">
              <tr>
                <th className="py-3 px-4">Order / Req #</th>
                <th className="py-3 px-4">Restaurant Tenant</th>
                <th className="py-3 px-4">Order Date</th>
                <th className="py-3 px-4 text-center">Items</th>
                <th className="py-3 px-4 text-right">Approved Qty</th>
                <th className="py-3 px-4 text-right">Dispatched Qty</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-neutral-700/60">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-400">
                    <span className="loading loading-spinner loading-md text-purple-600"></span>
                    <p className="text-xs mt-2">Loading supply orders...</p>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-400">
                    <IconPackage className="mx-auto mb-2 opacity-30" size={40} />
                    <p className="font-medium text-gray-600 dark:text-gray-300">No supply orders found</p>
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id} className="hover:bg-gray-50/50 dark:hover:bg-neutral-700/30">
                    <td className="py-3 px-4 font-mono font-semibold text-purple-600">
                      {o.requisition_no}
                    </td>

                    <td className="py-3 px-4">
                      <div className="font-semibold text-gray-800 dark:text-gray-100">
                        {o.tenant_name}
                      </div>
                      <div className="text-xs text-gray-400">
                        {o.tenant_phone || o.tenant_email || `Tenant #${o.tenant_id}`}
                      </div>
                    </td>

                    <td className="py-3 px-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
                      {o.provider_order_date
                        ? new Date(o.provider_order_date).toLocaleDateString()
                        : new Date(o.request_date).toLocaleDateString()}
                    </td>

                    <td className="py-3 px-4 text-center">
                      <span className="badge badge-sm badge-neutral font-mono">{o.item_count}</span>
                    </td>

                    <td className="py-3 px-4 text-right font-medium">
                      {Number(o.total_approved_qty || 0)}
                    </td>

                    <td className="py-3 px-4 text-right font-bold text-purple-600">
                      {Number(o.total_dispatched_qty || 0)}
                    </td>

                    <td className="py-3 px-4 whitespace-nowrap">
                      {o.status === "provider_ordered" && (
                        <span className="badge badge-warning badge-sm">Awaiting Dispatch</span>
                      )}
                      {o.status === "provider_dispatched" && (
                        <span className="badge badge-accent badge-sm">Dispatched</span>
                      )}
                      {o.status === "received" && (
                        <span className="badge badge-success badge-sm text-white">Delivered & Confirmed</span>
                      )}
                    </td>

                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {o.status === "provider_ordered" ? (
                        <button
                          onClick={() => handleOpenDispatchModal(o)}
                          className="btn btn-xs btn-primary bg-purple-600 hover:bg-purple-700 text-white border-none rounded-lg flex items-center gap-1 shadow-sm"
                        >
                          <IconTruck size={14} />
                          Dispatch Order
                        </button>
                      ) : (
                        <button
                          onClick={() => handleOpenDispatchModal(o)}
                          className="btn btn-xs btn-ghost text-gray-500 rounded-lg"
                        >
                          <IconEye size={14} /> View
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {orders.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-900/30">
            <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
              <span>
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} orders
              </span>
            </div>

            <div className="join">
              <button
                className="join-item btn btn-xs"
                disabled={page <= 1}
                onClick={() => setPage(1)}
              >
                <IconChevronsLeft size={14} />
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <IconChevronLeft size={14} />
              </button>
              <button className="join-item btn btn-xs no-animation font-semibold">
                Page {page} of {totalPages}
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <IconChevronRight size={14} />
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                <IconChevronsRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dispatch Modal */}
      {isDispatchModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-neutral-800 rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl border border-gray-100 dark:border-neutral-700 animate-in fade-in my-8">
            <div className="p-6 border-b border-gray-100 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <IconTruck className="text-purple-600" />
                  Dispatch Supply Order ({orderDetail?.requisition_no})
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Tenant: <span className="font-semibold">{orderDetail?.tenant_name}</span>
                </p>
              </div>
              <button
                onClick={() => setIsDispatchModalOpen(false)}
                className="btn btn-sm btn-ghost btn-circle"
              >
                <IconX size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {isLoadingDetail ? (
                <div className="text-center py-8">
                  <span className="loading loading-spinner loading-md text-purple-600"></span>
                </div>
              ) : (
                <>
                  {/* Items List */}
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500 mb-2 block">
                      Ordered Line Items
                    </label>
                    <div className="space-y-2">
                      {itemDispatches.map((it, idx) => (
                        <div
                          key={it.id}
                          className="flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900/40"
                        >
                          <div>
                            <div className="font-semibold text-sm">{it.item_title}</div>
                            <div className="text-xs text-gray-400">
                              Approved Quantity: <span className="font-bold">{it.approved_quantity}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500">Dispatching:</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              disabled={orderDetail?.status !== "provider_ordered"}
                              value={it.dispatched_quantity}
                              onChange={(e) => {
                                const updated = [...itemDispatches];
                                updated[idx].dispatched_quantity = e.target.value;
                                setItemDispatches(updated);
                              }}
                              className="input input-bordered input-xs w-24 text-right font-bold text-purple-600 rounded-lg"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Dispatch Notes */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                      Dispatch / Courier / Delivery Notes
                    </label>
                    <textarea
                      rows={2}
                      disabled={orderDetail?.status !== "provider_ordered"}
                      placeholder="e.g. Dispatched via Express Courier. Tracking: EXP-88219. Driver: Ram (9800000000)"
                      value={dispatchNotes || orderDetail?.provider_notes || ""}
                      onChange={(e) => setDispatchNotes(e.target.value)}
                      className="textarea textarea-bordered w-full rounded-xl text-sm"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 dark:border-neutral-700 flex items-center justify-between bg-gray-50/50 dark:bg-neutral-900/30">
              <button
                type="button"
                onClick={() => setIsDispatchModalOpen(false)}
                className="btn btn-sm btn-ghost rounded-xl"
              >
                Close
              </button>

              {orderDetail?.status === "provider_ordered" && (
                <button
                  type="button"
                  disabled={isSubmitting || isLoadingDetail}
                  onClick={handleConfirmDispatch}
                  className="btn btn-sm btn-primary bg-purple-600 hover:bg-purple-700 text-white border-none rounded-xl shadow-sm flex items-center gap-1.5"
                >
                  {isSubmitting ? (
                    <span className="loading loading-spinner loading-xs"></span>
                  ) : (
                    <IconCheck size={16} />
                  )}
                  Confirm Dispatch & Notify Restaurant
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
